import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material'
import {
  Upload as UploadIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  DateRange as DateRangeIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'

import dayjs from 'dayjs'
import { apiService } from '../services/apiService'
import { shipmentAPI } from '../services/api'

const ShipmentForm = ({ onSubmitSuccess, onError, projectId }) => {
  const [formData, setFormData] = useState({
    masterAwbNumber: '',
    shipmentCreationDate: dayjs(),
    childAwbNumbers: [],
    csvFile: null,
    csvData: [],
  })
  const [childAwbInput, setChildAwbInput] = useState('')
  const [csvPreview, setCsvPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  // Handle Master AWB input
  const handleMasterAwbChange = (event) => {
    setFormData(prev => ({
      ...prev,
      masterAwbNumber: event.target.value.trim().toUpperCase()
    }))
    setMessage({ type: '', text: '' })
  }

  // Handle Child AWB operations
  const handleAddChildAwb = () => {
    const awb = childAwbInput.trim().toUpperCase()
    if (awb && !formData.childAwbNumbers.includes(awb)) {
      setFormData(prev => ({
        ...prev,
        childAwbNumbers: [...prev.childAwbNumbers, awb]
      }))
      setChildAwbInput('')
      setMessage({ type: '', text: '' })
    }
  }

  const handleRemoveChildAwb = (awbToRemove) => {
    setFormData(prev => ({
      ...prev,
      childAwbNumbers: prev.childAwbNumbers.filter(awb => awb !== awbToRemove)
    }))
  }

  const handleChildAwbKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleAddChildAwb()
    }
  }

  // Handle CSV file upload (single file for all child AWBs)
  const handleCsvUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!file.name.endsWith('.csv')) {
      setMessage({ type: 'error', text: 'Please upload a CSV file' })
      return
    }

    if (!projectId) {
      setMessage({ type: 'error', text: 'No project selected' })
      return
    }

    setLoading(true)
    try {
      const response = await shipmentAPI.uploadCsv(file, projectId)
      const { data } = response.data

      setCsvPreview({
        rowCount: data.length,
        columns: response.data.columns,
        preview: response.data.data
      })

      setFormData(prev => ({
        ...prev,
        csvFile: file,
        csvData: data
      }))

      setMessage({ 
        type: 'success', 
        text: `CSV uploaded successfully! Found ${data.length} shipments (shared across all Child AWBs)` 
      })
    } catch (error) {
      console.error('CSV upload error:', error)
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to upload CSV file' 
      })
    } finally {
      setLoading(false)
    }
  }

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault()

    // Validation
    if (!formData.masterAwbNumber.trim()) {
      setMessage({ type: 'error', text: 'Please enter a Master AWB number' })
      return
    }

    if (!formData.shipmentCreationDate) {
      setMessage({ type: 'error', text: 'Please select a shipment creation date' })
      return
    }

    if (formData.csvData.length === 0) {
      setMessage({ type: 'error', text: 'Please upload a CSV file with shipment data' })
      return
    }

    setLoading(true)
    try {
      // Filter CSV data to only include required fields and remove empty values
      const filteredShipments = formData.csvData
        .filter(shipment => 
          shipment.order_id && 
          shipment.shipment_id && 
          shipment.order_id.toString().trim() !== '' && 
          shipment.shipment_id.toString().trim() !== ''
        )
        .map(shipment => ({
          order_id: shipment.order_id.toString().trim(),
          shipment_id: shipment.shipment_id.toString().trim()
        }))

      // Check if any shipments were filtered out
      const originalCount = formData.csvData.length
      const filteredCount = filteredShipments.length
      if (filteredCount < originalCount) {
        console.log(`Filtered out ${originalCount - filteredCount} shipments with empty values`)
      }

      if (filteredShipments.length === 0) {
        setMessage({ type: 'error', text: 'No valid shipments found. Please check your CSV data for empty order_id or shipment_id values.' })
        setLoading(false)
        return
      }

      const submitData = {
        project_id: projectId,
        master_awb_number: formData.masterAwbNumber,
        shipment_creation_date: formData.shipmentCreationDate.format('YYYY-MM-DD'),
        child_awbs: formData.childAwbNumbers.map(childAwbNumber => ({
          child_awb_number: childAwbNumber
        })),
        shipments: filteredShipments
      }

      console.log('DEBUG: Project ID:', projectId)
      console.log('DEBUG: Original shipments count:', originalCount)
      console.log('DEBUG: Filtered shipments count:', filteredCount)
      console.log('DEBUG: Submitting data:', submitData)
      
      const response = await apiService.createMasterAWB(submitData)
      
      console.log('DEBUG: Response:', response)
      
      const childAwbCount = formData.childAwbNumbers.length
      const childAwbText = childAwbCount > 0 ? `${childAwbCount} child AWBs and ` : ''
      setMessage({ 
        type: 'success', 
        text: `Successfully created Master AWB: ${response.master_awb_number || formData.masterAwbNumber} (ID: ${response.id}) with ${childAwbText}${filteredCount} shipments!` 
      })

      // Reset form
      setFormData({
        masterAwbNumber: '',
        shipmentCreationDate: dayjs(),
        childAwbNumbers: [],
        csvFile: null,
        csvData: [],
      })
      setChildAwbInput('')
      setCsvPreview(null)
      
      // Notify parent component
      if (onSubmitSuccess) {
        onSubmitSuccess(response)
      }

    } catch (error) {
      console.error('Submit error:', error)
      const errorMessage = error.response?.data?.detail || 'Failed to create shipments'
      setMessage({ type: 'error', text: errorMessage })
      
      if (onError) {
        onError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUploadIcon />
        Create Master AWB Shipments
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Master AWB Input */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Master AWB Number
            </Typography>
            
            <TextField
              label="Master AWB Number"
              value={formData.masterAwbNumber}
              onChange={handleMasterAwbChange}
              fullWidth
              size="small"
              placeholder="e.g., MAWB123456"
              required
            />
          </Grid>

          {/* Shipment Creation Date */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Shipment Creation Date
            </Typography>
            
            <TextField
              label="Creation Date"
              type="date"
              value={formData.shipmentCreationDate ? formData.shipmentCreationDate.format('YYYY-MM-DD') : ''}
              onChange={(e) => setFormData(prev => ({ ...prev, shipmentCreationDate: e.target.value ? dayjs(e.target.value) : null }))}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Child AWBs Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Child AWB Numbers (Optional)
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Enter Child AWB Number"
                value={childAwbInput}
                onChange={(e) => setChildAwbInput(e.target.value)}
                onKeyPress={handleChildAwbKeyPress}
                fullWidth
                size="small"
                placeholder="e.g., CAWB123456"
              />
              <Button
                variant="contained"
                onClick={handleAddChildAwb}
                disabled={!childAwbInput.trim()}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                <AddIcon />
              </Button>
            </Box>

            {/* Child AWB Chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {formData.childAwbNumbers.map((awb) => (
                <Chip
                  key={awb}
                  label={awb}
                  onDelete={() => handleRemoveChildAwb(awb)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            {formData.childAwbNumbers.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No child AWBs added yet. You can create a Master AWB without child AWBs.
              </Typography>
            )}
          </Grid>

          {/* CSV Upload Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              CSV File Upload (For All Child AWBs)
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                disabled={loading}
              >
                Choose CSV File
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleCsvUpload}
                />
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CloudUploadIcon />}
                onClick={() => {
                  const sample = 'order_id,shipment_id\nORD001,SHIP001\nORD002,SHIP002\n';
                  const blob = new Blob([sample], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'sample_shipments.csv';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                sx={{ ml: 0 }}
              >
                Download Sample CSV
              </Button>

              {formData.csvFile && (
                <Typography variant="body2" color="textSecondary">
                  {formData.csvFile.name}
                </Typography>
              )}
            </Box>

            {/* CSV Preview */}
            {csvPreview && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  CSV Preview: {csvPreview.rowCount} rows, Columns: {csvPreview.columns.join(', ')}
                </Alert>
                
                <Typography variant="subtitle2" gutterBottom>
                  First few rows:
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <pre style={{ fontSize: '0.75rem', margin: 0 }}>
                    {JSON.stringify(csvPreview.preview, null, 2)}
                  </pre>
                </Box>
              </Box>
            )}
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || !formData.masterAwbNumber.trim() || formData.csvData.length === 0}
                startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              >
                {loading ? 'Creating...' : 'Create Master AWB'}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Messages */}
        {message.text && (
          <Alert 
            severity={message.type} 
            sx={{ mt: 2 }}
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}
      </Box>
    </Paper>
  )
}

export default ShipmentForm